import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";

export type SubjectKind = components["schemas"]["SubjectSearchResult"]["kind"];

export interface Subject {
  id: string;
  name: string;
  kind: SubjectKind;
  /** The agent-defined type name (e.g. "equipment") — only for kind "custom". */
  customType?: string;
  /** When the subject was created; present on search results. */
  createdAt?: string;
  /** The owner's name — only for kind "patient" with a linked client. */
  clientName?: string;
}

export const kindLabel = (subject: Subject) =>
  subject.kind === "custom" && subject.customType
    ? subject.customType
    : subject.kind.replace(/_/g, " ");

const toSubject = (
  s: components["schemas"]["SubjectSearchResult"],
): Subject => ({
  id: s.entity_uuid,
  name: s.name,
  kind: s.kind,
  customType: s.custom_type ?? undefined,
  createdAt: s.created_at,
  clientName: s.client_name ?? undefined,
});

export const subjectsClient = {
  search: (q: string): Promise<Subject[]> =>
    request(() =>
      apiClient.GET("/subjects/search", {
        params: { query: q ? { q } : {} },
      }),
    ).then((rows) => rows.map(toSubject)),

  /** A single subject by id, for rehydrating the URL-held selection on
   * refresh/deep-link. Returns the same shape as a search row. */
  get: (id: string): Promise<Subject> =>
    request(() =>
      apiClient.GET("/subjects/{entity_uuid}", {
        params: { path: { entity_uuid: id } },
      }),
    ).then(toSubject),
};
